import { Component, OnInit } from '@angular/core';
import { ToasterService } from 'src/app/_services/toaster.service';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AssessmentStatus, ImpairmentCategory, TwoDimensionalAssessmentResult, TwoDimensionalAiAssessmentViewModel, TwoDimensionalAiAssessmentViewModelRead, SpeedUnit, DistanceUnit, WeightUOM } from 'src/app/_models/generatedModels';
import { AssessmentService } from 'src/app/_services/generatedServices';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { getParamFromRoute } from 'src/app/_helpers/extensions.module';
import { BreadcrumbsService } from 'src/app/_services/breadcrumbs.service';
import { FormCanDeactivate } from 'src/app/_guards/canDeactivate.guard';
import { VideoPopupExampleComponent } from './video-popup-example.component';

@Component({
  selector: 'bxl-add-2d-ai-assessment',
  templateUrl: 'add-2d-ai-assessment.component.html',
  styleUrls: ['add-2d-ai-assessment.component.scss'],
})
export class Add2DAiAssessmentComponent extends FormCanDeactivate implements OnInit {
  initialized = false;
  assessment: TwoDimensionalAiAssessmentViewModelRead;
  twoDAssessmentResult = TwoDimensionalAssessmentResult;
  distanceUnit = DistanceUnit;
  speedUnit = SpeedUnit;
  weightUnit = WeightUOM;
  assessmentStatus = AssessmentStatus;
  ImpairmentCategory = ImpairmentCategory;
  public formGroup: FormGroup;
  submitComplete: Promise<{}> | undefined;
  clientUserId: number;
  leftOption: any = { label: 'Needs Attention', value: 'NeedsAttention' };
  rightOption: any = { label: 'Satisfactory', value: 'Satisfactory' };

  constructor(private route: ActivatedRoute, private router: Router, private toastr: ToasterService, private modalService: NgbModal, private assessmentService: AssessmentService, private fb: FormBuilder, private breadcrumbs: BreadcrumbsService) {
    super();
  }

  ngOnInit(): void
  {
    this.clientUserId = getParamFromRoute(this.route, 'id');
    this.setupForm();
  }

  setupForm(): any
  {
    let videos = this.fb.array([this.initializeVideo(), this.initializeVideo()], [this.twoVideosValidator()]);

    this.formGroup = this.fb.group({
      title: [this.assessment?.title, [Validators.required]],
      assessmentNotes: [this.assessment?.assessmentNotes],
      videos: videos,
      speed: [null],
      speedUnit: [2],
      runnerWeight: [null, [Validators.required]],
      weightUnit: [1],
      runnerHeight: [null, [Validators.required]],
      heightUnit: [2]
    });

    this.formGroup.markFormDirtyOnValueChange().subscribe();
    this.initialized = true;
  }

  initializeVideo(): FormGroup {
    return this.fb.group({
      id: [0],
      videoId: [null],
      description: [null]
    });
  }

  onDecimalInput(event: Event)
  {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9.]/g, '');
  }

  onSave() {
    if (!this.formGroup.valid) {
      this.formGroup.markAllControlsDirty();
      this.toastr.error('Please fill out all required fields', 'Error');
      return;
    }

    this.formGroup.markAsPristine();
    this.submitComplete = new Promise((resetButton:any, reject) => {
      const formData: TwoDimensionalAiAssessmentViewModel = Object.assign({}, this.formGroup.value, this.formGroup.value);
      formData.videos = formData.videos.filter(x => x.videoId != null);
      this.save(formData, resetButton);
    });
  }

  save(formData: TwoDimensionalAiAssessmentViewModel, resetButton: () => any)
  {
    if (!formData.speed)
      formData.speed = 0;

    this.assessmentService.createTwoDimensionalAiAssessment(this.clientUserId, formData).subscribe(
      (assessment) => {
        this.toastr.success('Your videos are being processed, which usually takes 5-10 minutes depending on the number of videos in the queue', 'Success');
        this.navigateBack();
        resetButton();
      },
      (error) => resetButton()
    );
  }

  cancel() {
    this.navigateBack();
  }

  navigateBack()
  {
    this.router.navigate(['../../list'], { relativeTo: this.route });
  }

  twoVideosValidator(): ValidatorFn
  {
    return (control: AbstractControl): ValidationErrors | null =>
    {
      const videos = control as FormArray;
      const isValid = videos.controls.every(video => video.get('videoId')?.value);
      return isValid ? null : { twoVideosRequired: true };
    };
  }

  showSideExampleVideo()
  {
    const modalRef = this.modalService.open(VideoPopupExampleComponent, { size: 'lg', ariaLabelledBy: 'modal-basic-title' });
    modalRef.componentInstance.analysisName = 'Side view video tutorial';
    modalRef.componentInstance.videoLink = "https://wv-cdn-00-00.wowza.com/ea8c6bfa-c593-4e7f-9cb5-63eeeb7b20ec/cmaf/15c7fd9a-4d31-4f6a-b3e9-cc10e46a9baa/playlist.m3u8";
    modalRef.componentInstance.description = '';
    modalRef.componentInstance.encoded = true;

    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }

  showBackExampleVideo()
  {
    const modalRef = this.modalService.open(VideoPopupExampleComponent, { size: 'lg', ariaLabelledBy: 'modal-basic-title' });
    modalRef.componentInstance.analysisName = 'Back view video tutorial';
    modalRef.componentInstance.videoLink = "https://wv-cdn-00-00.wowza.com/ea8c6bfa-c593-4e7f-9cb5-63eeeb7b20ec/cmaf/13f7fdf5-3cb3-4cd7-8b76-e55c12420c2d/playlist.m3u8";
    modalRef.componentInstance.encoded = true;
    modalRef.componentInstance.description = '';

    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }
}
